import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { DollarSign, TrendingUp, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";

interface AISpendingData {
  org_id: string;
  period_start: string;
  limit_amount: number;
  total_spent: number;
}

interface AISpendingManagerProps {
  orgId: string;
  orgName: string;
}

export function AISpendingManager({ orgId, orgName }: AISpendingManagerProps) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [spendingData, setSpendingData] = useState<AISpendingData | null>(null);
  const [newLimit, setNewLimit] = useState<string>("");

  useEffect(() => {
    if (open) {
      loadSpendingData();
    }
  }, [open, orgId]);

  const loadSpendingData = async () => {
    setLoading(true);
    try {
      // Get current period start (first day of current month)
      const periodStart = new Date();
      periodStart.setDate(1);
      periodStart.setHours(0, 0, 0, 0);

      const { data, error } = await supabase
        .from('org_ai_spending')
        .select('*')
        .eq('org_id', orgId)
        .gte('period_start', periodStart.toISOString())
        .single();

      if (error && error.code !== 'PGRST116') {
        throw error;
      }

      if (data) {
        setSpendingData(data);
        setNewLimit(data.limit_amount.toString());
      } else {
        // No data for current period - set defaults
        setSpendingData({
          org_id: orgId,
          period_start: periodStart.toISOString(),
          limit_amount: 100,
          total_spent: 0
        });
        setNewLimit("100");
      }
    } catch (error: any) {
      console.error('Failed to load spending data:', error);
      toast.error("Failed to load AI spending data");
    } finally {
      setLoading(false);
    }
  };

  const handleSaveLimit = async () => {
    const limitValue = parseFloat(newLimit);

    if (isNaN(limitValue) || limitValue < 0) {
      toast.error("Please enter a valid limit amount");
      return;
    }

    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('admin-update-ai-limit', {
        body: {
          orgId,
          newLimit: limitValue
        }
      });

      if (error) throw error;

      toast.success("AI spending limit updated successfully");
      await loadSpendingData();
    } catch (error: any) {
      console.error('Failed to update AI limit:', error);
      toast.error(error.message || "Failed to update AI spending limit");
    } finally {
      setLoading(false);
    }
  };

  const remaining = spendingData 
    ? Math.max(0, spendingData.limit_amount - spendingData.total_spent)
    : 0;

  const percentUsed = spendingData && spendingData.limit_amount > 0
    ? Math.min(100, (spendingData.total_spent / spendingData.limit_amount) * 100)
    : 0;

  const isOverLimit = spendingData 
    ? spendingData.total_spent >= spendingData.limit_amount
    : false;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="gap-2">
          <DollarSign className="w-4 h-4" />
          AI Spending
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>AI Spending Management</DialogTitle>
          <DialogDescription>
            Monitor and configure AI usage limits for {orgName}
          </DialogDescription>
        </DialogHeader>

        {loading && !spendingData ? (
          <div className="py-8 text-center text-muted-foreground">
            Loading spending data...
          </div>
        ) : spendingData ? (
          <div className="space-y-6 py-4">
            {/* Current Usage Card */}
            <Card className={`p-4 ${isOverLimit ? 'border-destructive' : 'border-border'}`}>
              <div className="space-y-4">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Current Period Usage</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      Period: {new Date(spendingData.period_start).toLocaleDateString()} - Present
                    </p>
                  </div>
                  {isOverLimit && (
                    <AlertCircle className="w-5 h-5 text-destructive" />
                  )}
                </div>

                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Usage</span>
                    <span className="font-medium">
                      ${spendingData.total_spent.toFixed(2)} / ${spendingData.limit_amount.toFixed(2)}
                    </span>
                  </div>
                  <Progress 
                    value={percentUsed} 
                    className={isOverLimit ? "bg-destructive/20" : ""}
                  />
                  <div className="flex justify-between text-xs text-muted-foreground">
                    <span>{percentUsed.toFixed(1)}% used</span>
                    <span>${remaining.toFixed(2)} remaining</span>
                  </div>
                </div>

                {isOverLimit && (
                  <div className="bg-destructive/10 border border-destructive/20 rounded p-3">
                    <p className="text-sm text-destructive font-medium">
                      ⚠️ Limit Exceeded
                    </p>
                    <p className="text-xs text-destructive/80 mt-1">
                      AI calls are currently blocked for this organization until the limit is raised.
                    </p>
                  </div>
                )}
              </div>
            </Card>

            {/* Update Limit */}
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="ai-limit">Monthly AI Spending Limit ($)</Label>
                <Input
                  id="ai-limit"
                  type="number"
                  min="0"
                  step="10"
                  value={newLimit}
                  onChange={(e) => setNewLimit(e.target.value)}
                  placeholder="100.00"
                />
                <p className="text-xs text-muted-foreground">
                  Set the maximum amount this organization can spend on AI API calls per month.
                </p>
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setOpen(false)}>
                  Cancel
                </Button>
                <Button 
                  onClick={handleSaveLimit} 
                  disabled={loading}
                  className="gap-2"
                >
                  <TrendingUp className="w-4 h-4" />
                  {loading ? "Saving..." : "Update Limit"}
                </Button>
              </div>
            </div>
          </div>
        ) : (
          <div className="py-8 text-center text-muted-foreground">
            No spending data available
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
